import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, Grid, List, Filter, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ProductCard } from '../components/ProductCard';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('newest');

  const selectedCategory = searchParams.get('category') || '';
  const searchQuery = searchParams.get('search') || '';
  usePageTitle(selectedCategory || (searchQuery ? `Search: ${searchQuery}` : 'Shop All'));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedCategory) params.append('category', selectedCategory);
        if (searchQuery) params.append('search', searchQuery);
        
        const [productsRes, categoriesRes] = await Promise.all([
          axios.get(`${API_URL}/products?${params.toString()}`),
          axios.get(`${API_URL}/categories`)
        ]);
        
        let sortedProducts = [...productsRes.data];
        switch (sortBy) {
          case 'price-low':
            sortedProducts.sort((a, b) => (a.sale_price || a.price) - (b.sale_price || b.price));
            break;
          case 'price-high':
            sortedProducts.sort((a, b) => (b.sale_price || b.price) - (a.sale_price || a.price));
            break;
          case 'rating':
            sortedProducts.sort((a, b) => b.avg_rating - a.avg_rating);
            break;
          default:
            // newest - already sorted by created_at
            break;
        }
        
        setProducts(sortedProducts);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory, searchQuery, sortBy]);

  const handleCategoryChange = (category) => {
    if (category === selectedCategory) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  return (
    <div className="page-transition min-h-screen">
      {/* Header */}
      <div className="bg-[#0B1120] text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="breadcrumb mb-4">
            <Link to="/" className="text-white/60 hover:text-white">Home</Link>
            <span className="text-white/40">/</span>
            <span className="text-white">Shop</span>
            {selectedCategory && (
              <>
                <span className="text-white/40">/</span>
                <span className="text-white">{selectedCategory}</span>
              </>
            )}
          </nav>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="shop-title">
            {selectedCategory || (searchQuery ? `Results for "${searchQuery}"` : 'All Products')}
          </h1>
          <p className="text-white/70 mt-2">
            {products.length} {products.length === 1 ? 'product' : 'products'} found
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-8">
              {/* Categories */}
              <div>
                <h3 className="font-semibold text-foreground mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Categories
                </h3>
                <div className="space-y-3">
                  {categories.map((category) => (
                    <label 
                      key={category.id} 
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <Checkbox
                        checked={selectedCategory === category.name}
                        onCheckedChange={() => handleCategoryChange(category.name)}
                        data-testid={`filter-category-${category.id}`}
                      />
                      <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                        {category.name} ({category.product_count})
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              {(selectedCategory || searchQuery) && (
                <Button 
                  variant="outline" 
                  onClick={clearFilters}
                  className="w-full"
                  data-testid="clear-filters-btn"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              {/* Mobile Filter Toggle */}
              <Button 
                variant="outline" 
                className="lg:hidden"
                onClick={() => setMobileFiltersOpen(true)}
                data-testid="mobile-filters-toggle"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>

              {/* Sort */}
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-sm text-muted-foreground">Sort by:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[160px]" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price-low">Price: Low to High</SelectItem>
                    <SelectItem value="price-high">Price: High to Low</SelectItem>
                    <SelectItem value="rating">Top Rated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[3/4] bg-muted rounded-xl mb-4" />
                    <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground mb-4">No products found</p>
                <Button onClick={clearFilters} variant="outline">
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
                {products.map((product, index) => (
                  <div 
                    key={product.id}
                    className="stagger-item"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileFiltersOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-80 bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold" style={{ fontFamily: "'Playfair Display', serif" }}>
                Filters
              </h2>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setMobileFiltersOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Categories */}
            <div className="space-y-4">
              <h3 className="font-medium text-foreground">Categories</h3>
              <div className="space-y-3">
                {categories.map((category) => (
                  <label 
                    key={category.id} 
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedCategory === category.name}
                      onCheckedChange={() => {
                        handleCategoryChange(category.name);
                        setMobileFiltersOpen(false);
                      }}
                    />
                    <span className="text-sm">
                      {category.name} ({category.product_count})
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {(selectedCategory || searchQuery) && (
              <Button 
                variant="outline" 
                onClick={() => {
                  clearFilters();
                  setMobileFiltersOpen(false);
                }}
                className="w-full mt-8"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
